import React from 'react';
import { ReportTitleInfo } from '@component/LightVersion/Reports/ReportTitleInfo';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartTableDynamic from '@component/LightVersion/Reports/dynamic';
import {
  colors,
  statusesRidesMembers,
} from '@component/LightVersion/Reports/dynamic/types';
import {
  formatMonthLabel,
  getYAxisConfig,
} from '@component/LightVersion/Reports/yAxisHelpers';
import { useNavigate } from 'react-router-dom';
import BarManyLinesChartSkeleton from '@src/uikit/Loader/DashboardSkeleton/BarManyLinesChartSkeleton';
import ChartTableDynamicSkeleton from '@src/uikit/Loader/DashboardSkeleton/ChartTableDynamicSkeleton';
import { removeCounty } from '@src/utils';
import { blurStyle } from '@component/LightVersion/Reports/helpers';
import {
  REPORT_CHART_TEXT_STYLE,
  REPORT_METRIC_TITLE_STYLE,
  REPORT_METRIC_VALUE_STYLE,
} from '@component/LightVersion/Reports/constants';

const RIDES_COLOR = '#9bb1ff';
const MEMBERS_COLOR = '#4c6ef5';

interface CountryData {
  country: string;
  rideCount: number;
}

interface RawDataOld {
  month: Record<string, CountryData[]>;
  total?: { country: string; rideCount: number }[];
}

interface MonthData {
  month: string;
  rideCount: number;
  memberCount: number;
  avgRidesPerMember: number | null;
}

interface RawDataNew {
  months: MonthData[];
  total: {
    rideCount: number;
    memberCount: number;
    avgRidesPerMember: number | null;
  };
}

type RawData = RawDataOld | RawDataNew;

interface DynamicChartProps {
  rawData?: RawData;
  title: string;
  tooltip?: string;
  navigateUrl?: string;
  loading?: boolean;
  isFetching?: boolean;
}

const isOldData = (data: RawData): data is RawDataOld =>
  !!(data as RawDataOld)?.month;

const keyLabelMap: Record<string, string> = {
  rideCount: 'Ride Count',
  memberCount: 'Member Count',
};

const formatThousands = (value: number) => {
  if (value === 0) return '0';
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
};

export const formatDate = (date: string) =>
  // format example -- January 4, 2026
  new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

export default function DynamicDashboardChart({
  rawData,
  title,
  tooltip,
  navigateUrl,
  loading,
  isFetching,
}: DynamicChartProps) {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div style={{ width: '100%' }}>
        <BarManyLinesChartSkeleton />
        <ChartTableDynamicSkeleton />
      </div>
    );
  }

  if (!rawData) return null;

  const isOld = isOldData(rawData);

  let chartData: any[] = [];
  let bars: { dataKey: string; fill: string; name?: string }[] = [];
  let totals: { title: string; value: string | number }[] = [];
  let tableData: any[] | null = null;

  if (isOld) {
    chartData = Object.entries(rawData.month).map(([monthKey, values]) => {
      const entry: any = {
        month: monthKey,
        label: formatMonthLabel(monthKey),
      };

      (values as CountryData[]).forEach((v) => {
        entry[v.country] = v.rideCount;
      });

      return entry;
    });

    const countries = new Set<string>();
    Object.values(rawData.month).forEach((arr) =>
      arr.forEach((item) => countries.add(item.country))
    );

    const colorsLocal = [
      '#9bb1ff',
      '#4c6ef5',
      '#82ca9d',
      '#ff7300',
      '#ffbb28',
      '#8884d8',
    ];
    let i = 0;
    bars = Array.from(countries).map((country) => ({
      dataKey: country,
      name: removeCounty(country),
      fill: colorsLocal[i++ % colorsLocal.length],
    }));

    totals =
      rawData.total?.map((item) => ({
        title: removeCounty(item.country),
        value: item.rideCount.toLocaleString(),
      })) || [];
  } else {
    const dataNew = rawData as RawDataNew;
    chartData = dataNew.months.map((m) => ({
      month: m.month,
      label: formatMonthLabel(m.month),
      rideCount: m.rideCount,
      memberCount: m.memberCount,
      avgRidesPerMember: m.avgRidesPerMember,
    }));

    bars = [
      { dataKey: 'rideCount', fill: RIDES_COLOR },
      { dataKey: 'memberCount', fill: MEMBERS_COLOR },
    ];

    totals = [
      {
        title: 'Total Rides',
        value: dataNew.total.rideCount.toLocaleString(),
      },
      {
        title: 'Distinct Members',
        value: dataNew.total.memberCount.toLocaleString(),
      },
      {
        title: 'Avg Rides per Member',
        value:
          dataNew.total.avgRidesPerMember != null
            ? dataNew.total.avgRidesPerMember.toFixed(2)
            : '0',
      },
    ];

    tableData = dataNew.months.map((m) => ({
      month: formatMonthLabel(m.month),
      members: { count: m.memberCount, percentage: 0, increase: false },
      rides: { count: m.rideCount, percentage: 0, increase: false },
    }));
  }

  const yValues: number[] = isOld
    ? chartData.flatMap((row) =>
        Object.entries(row)
          .filter(([key]) => key !== 'month' && key !== 'label')
          .map(([, value]) => Number(value) || 0)
      )
    : chartData.flatMap((row) => [
        Number(row.rideCount) || 0,
        Number(row.memberCount) || 0,
      ]);

  const { max, ticks } = getYAxisConfig(yValues);
  const handleNavigateViewDataClick = () => {
    if (navigateUrl) {
      navigate(navigateUrl);
    }
  };
  return (
    <>
      <div
        data-test-id="c-index-ee63"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          background: '#292850FF',
          borderRadius: '12px',
          padding: '20px',
          color: 'white',
          ...blurStyle(isFetching),
        }}
      >
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ marginBottom: '20px' }} className="chart-title">
              <ReportTitleInfo title={title} tooltip={tooltip} />
            </h3>
            <span
              className="report-view-data"
              onClick={handleNavigateViewDataClick}
              style={{
                cursor: 'pointer',
              }}
            >
              View Data
            </span>
          </div>

          <div style={{ display: 'flex' }}>
            <div style={{ flex: 6 }}>
              <ResponsiveContainer
                width="100%"
                height={400}
                style={{ display: 'flex' }}
              >
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid stroke="#ccc" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="#ccc"
                    tick={{
                      fill: '#ccc',
                      ...REPORT_CHART_TEXT_STYLE,
                    }}
                  />
                  <YAxis
                    stroke="#ccc"
                    domain={[0, max]}
                    ticks={ticks}
                    tickFormatter={(v) => formatThousands(Number(v))}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#1f223a',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    formatter={(value: any, name: any) => {
                      const prettyName =
                        keyLabelMap[name as string] ?? (name as string);
                      return [formatThousands(Number(value)), prettyName];
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      color: 'white',
                      paddingTop: '10px',
                      ...REPORT_CHART_TEXT_STYLE,
                    }}
                    formatter={(value) =>
                      keyLabelMap[value as string] ?? (value as string)
                    }
                  />
                  {bars.map((bar) => (
                    <Bar
                      key={bar.dataKey}
                      dataKey={bar.dataKey}
                      name={bar.name}
                      fill={bar.fill}
                      radius={[6, 6, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div
              style={{
                flex: 1,
                marginLeft: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '20px',
              }}
            >
              {totals.map((item) => (
                <SummaryCard
                  key={item.title}
                  title={item.title}
                  value={item?.value?.toString()}
                />
              ))}
            </div>
          </div>
        </>
      </div>

      {tableData && (
        <div style={{ width: '100%' }}>
          <ChartTableDynamic
            data={tableData}
            colors={{
              ...colors,
              completed: RIDES_COLOR,
              driverNoShow: MEMBERS_COLOR,
            }}
            statuses={statusesRidesMembers}
            isFetching={isFetching}
          />
        </div>
      )}
    </>
  );
}

function SummaryCard({ title, value }: { title: string; value: string }) {
  return (
    <div
      data-test-id="c-index-ee63"
      style={{
        background: '#57568EFF',
        border: '1px solid #ccc',
        borderRadius: '10px',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <h4
        style={{
          margin: '0 0 10px 0',
          ...REPORT_METRIC_TITLE_STYLE,
        }}
      >
        {title}
      </h4>
      <h2
        style={{
          margin: 0,
          ...REPORT_METRIC_VALUE_STYLE,
        }}
      >
        {value}
      </h2>
    </div>
  );
}
